import { useTranslation } from '@kostbar/i18n'

interface CountSummaryBarProps {
  matched: number
  variances: number
  open: number
}

/**
 * What the count amounts to, as one bar.
 *
 * It replaces a centred grey sentence that floated above the first section and repeated the counts
 * the section headings already carry (CHG-2026-09-inventory-count-review-ux). The bar answers
 * "how much of this is work?" before the counter starts scrolling; the legend keeps the exact
 * numbers, since a proportion alone is not a count.
 *
 * Colour is never the only signal — every segment is named and numbered in the legend below it.
 */
export function CountSummaryBar({
  matched,
  variances,
  open,
}: CountSummaryBarProps) {
  const { t } = useTranslation('inventory-count')
  const total = matched + variances + open
  if (total === 0) return null

  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-1.5 gap-0.5" aria-hidden>
        {matched > 0 && (
          <div
            className="rounded-l-full bg-[var(--delta-up)] last:rounded-r-full"
            style={{ flexGrow: matched }}
          />
        )}
        {variances > 0 && (
          <div
            className="bg-destructive first:rounded-l-full last:rounded-r-full"
            style={{ flexGrow: variances }}
          />
        )}
        {open > 0 && (
          <div
            className="rounded-r-full bg-ring first:rounded-l-full"
            style={{ flexGrow: open }}
          />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs tabular-nums text-muted-foreground">
        {matched > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="size-1.5 rounded-full bg-[var(--delta-up)]" />
            {t('review.summaryMatches', { count: matched })}
          </span>
        )}
        {variances > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="size-1.5 rounded-full bg-destructive" />
            {t('review.summaryVariances', { count: variances })}
          </span>
        )}
        {open > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="size-1.5 rounded-full bg-ring" />
            {t('review.summaryNotCounted', { count: open })}
          </span>
        )}
      </div>
    </div>
  )
}
